export interface SerializedCartItem{
	product_id: number,
	granel: boolean,
	quantity: number,
	redeem_points?: number,
	product_name?: string,
}

export interface CartItem{
	product_id: number,
	product_name: string,
	size: string,
	price: number,
	price_discounted?: number,
	price_gram?: number,
	quantity: number,
	redeem_points?: number,
}

export interface APIResponse<T>{
	error: boolean,
	code: string,
	message: string,
	data?: T
}

export interface WebCategory{
	category_id: number,
	category_name: string,
	priority: number,
	banner?: string,
}

export interface ShopPayload{
	query?: string,
	page_title: string,
	page_description: string,
	filters: {
		filter_name: string,
		filter_values: string[]
	}[],
	products: {
		product_id: number,
		product_name: string,
		price: number,
		price_discounted?: number,
		price_gram?: number,
		size: string,
		message: string,
		images: string[],
		categories: number[],
	}[],
	categories: {
		category_id: number,
		category_name: string,
	}[]
}

export interface WebProduct{
	product_id: number,
	product_name: string,
	price: number,
	price_discounted?: number,
	price_gram?: number,
	size: string,
	message: string,
	description: string,
	images: string[],
	categories: {
		category_id: number,
		category_name: string,
		main: boolean,
	}[],
	metadata: {
		metadata_id: number,
		metadata_name: string,
		metadata_value_id: number,
		metadata_value: string,
	}[],
	related?: ShopPayload["products"]
}

export interface ShippingMethod{
	method_id: number,
	name: string,
	cost: number,
	description?: string,
	local?: boolean,
	needs_location?: boolean,
	min_total: number,
	location?: Location,
}

export interface ShopVariables{
	shipping_limit: number,
	shipping_cost: number,
	shipping_end: number,
	shipping_enabled: boolean,
	announcement: string,
	landing_category: number
	landing_category_title: string,
	shipping_info: string,
	store_schedule: string,

	pickup_enabled: boolean,
	pickup_start: number,
	pickup_end: number,

	force_openpay: number,
}

export interface WebData{
	variables: ShopVariables,
	landing_products: ShopPayload["products"],
	users: {
		user_id: number,
		first_name: string,
		last_name: string,
		department: string,
	}[]
}

export enum ShippingType{
	SHIPPING = 1,
	PICKUP = 20,
}

export interface InvoiceForm{
	order_hash: string,
	email: string,
	legal_name: string,
	rfc: string,
	regimen: string,
	use_cfdi: string,
	address: Location
}

export interface Location{
	location_id?: number,
	name?: string,
	phone?: string,
	street: string,
	exterior_number: string,
	interior_number?: string,
	neighborhood: string,
	city: string,
	state: string,
	zipcode: string,
	country?: string,
	lattitude?: number,
	longitude?: number,
	place_id?: string,
	comments?: string,
}

export interface Order{
	order_id: number,
	order_hash: string,
	owner_name: string,
	owner_last_name: string,
	owner_email: string,
	owner_phone: string,
	shipping_method: number,
	shipping_method_name: string,
	shipping_method_type: number,
	shipping_method_location_id: number,
	shipping_location_id: number,
	shipping_date: number,
	gift_name: string,
	gift_last_name: string,
	gift_message: string,
	gift_phone?: string,
	shipping_cost: number,
	total: number,
	paid: boolean,
	shipped: boolean,
	prepared: boolean,
	date_created: number,
	shipping_location: Location,
	shipping_method_location: Location,
	items: {
		item_id: number,
		product_id: number,
		product_name: string,
		size: string,
		quantity: number,
		cost_single: number,
		cost: number
	}[],
	payments: {
		payment_id: number,
		payment_method: number,
		method_name: string,
		amount: number,
		paid: boolean,
	}[],
}

export interface OrderFile {
	asset_id: number,
	name: string,
	description: string,
	file_url: string,
}

export interface OrderData {
	order_id: number,
	order_hash: string,
	order_method?: number,
	owner_name: string,
	owner_last_name: string,
	owner_email: string,
	owner_phone: string,
	shipping_method: number,
	shipping_method_name: string,
	shipping_method_type: number,
	shipping_method_location_id: number,
	shipping_location_id: number,
	shipping_date: number,
	gift_name: string,
	gift_last_name: string,
	gift_message: string,
	gift_phone: string,
	shipping_cost: number,
	total: number,
	paid: boolean,
	shipped: boolean,
	prepared: boolean,
	date_created: number,
	date_paid: number,
	quotation: number,
	items: {
		item_id: number,
		product_id: number,
		product_name: string,
		size: string,
		quantity: number,
		cost_single: number,
		cost: number,
	}[],
	payments: {
		payment_id: number,
		payment_method: number,
		method_name: string,
		amount: number,
		date_paid: number,
		paid: boolean,
	}[],
	files?: OrderFile[],
}

export interface SitePage{
	page_id: number,
	page_url: string,
	page_title: string,
	page_subtitle?: string,
	button_text: string,
	button_url: string,
	page_contents?: string,
	page_type: number,
	deleted: boolean,
	user_creator: number,
	user_deleter?: number,
	date_created?: number,
	date_deleted?: number,
	assets?:  SitePageAsset[],
	button?: boolean,
	banner?: string,
}

export enum SitePageType{
	PDF = 1,
	IMAGE_GALLERY = 2,
	TEXT = 3
}

export interface SitePageAssetType{
	type_id: number,
	type_name: string,
}

export enum AssetType{
	PDF = 1,
	IMAGE= 2,
}

export interface SitePageAsset{
	page_id?: number,
	asset_id?: number,
	asset_type: number,
	asset_name: string,
	asset_url: string,
	asset_description?: string | any,
	deleted: boolean,
	user_creator?: number,
	user_deleter?: number,
	date_created?: number,
	date_deleted?: number,
}

export interface PublicCareer{
	career_id: number,
	title: string,
	contact: string,
	description?: string,
	image?: string,
}

export interface UserLogin{
	user_id: number,
	email: string,
	first_name: string,
	last_name: string,
	name: string,
	superadmin: boolean,
	change_password: boolean,
	phone: string,
	birthday?: number,
	gender_id?: number,
	email_confirmed: boolean,
}

export interface TokenData{ 
	access: string, 
	expires: number,
}

export interface UserCredit{
	credit_id: number;
	payment_id?: number;
	amount: number;
	deleted: number;
	date_created: number;
	date_deleted?: number;
	payment_amount?: number;
	order_id?: number;
	order_key?: string;
	order_hash?: string;
}

export interface UserCreditsBalance{
	collected: number;
	used: number;
	available: number;
	last_purchase?: number;
	expires?: number;
}

export interface LevelProduct{
	product_id: number;
	name: string;
	price_online: number;
	redeem_points: number;
	image_url?: string;
}

export interface UserLevel{
	level_id: number;
	level_name: string;
	points: number;
	unlocked?: boolean;
	products: LevelProduct[];
}

export interface UserLevelsData{
	total_points: number;
	levels: UserLevel[];
}

export interface UserOrder{
	order_id: number;
	order_hash: string;
	order_method: number;
	shipping_method: number;
	shipping_type: number;
	owner_name: string;
	owner_last_name: string;
	owner_email: string;
	owner_phone: string;
	date_created: number;
	date_paid: number;
	shipping_date: number;
	total: number;
	prepared: boolean;
	shipped: boolean;
	paid: boolean;
}

export interface CheckoutDeliveryResult{
	paid: boolean;
	total: number;
	shipping_cost: number;
}

export interface CheckoutPaymentResult{
	redirect: string;
}

export interface CheckoutResponseResult{
	paid: boolean;
	message: string;
	redirect_url: string;
}

export interface CheckoutLinkResult{
	order_hash: string;
}

export interface ClaimCreditsResult{
	credit_id: number;
	amount: number;
}

export interface InvoiceResult{
	invoice_url: string;
	created: boolean;
}
export interface UpdatePasswordData {
	currentPassword: string;
	newPassword: string;
	confirm: string;
}

export interface UpdateUserData {
	last_name: string;
	first_name: string;
	phone: string;
	birthday?: number;
	gender_id?: number;
}
